import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Alert,
  useTheme,
  useMediaQuery,
  darken,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import InputMask from "react-input-mask";
import { LoadingButton } from "@mui/lab";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useHttpEscalloChatService } from "../../service/http.escallo-chat.service";
import { selectToken } from "../../features/token/tokenSlice";
import {
  selectAlreadyLoggedByParam,
  setAlreadyLoggedByParam,
  setCredentials,
} from "../../features/credentials/credentialsSlice";
import { isValidCPF, lightOrDark } from "../../functions";
import { setChatState } from "../../features/settings/settingsSlice";
import { AppLoading } from "../../AppLoading";
import { v4 as uuidv4 } from "uuid";
import { cpfRegExp, phoneRegExp } from "../../constants/regex";

export type LoginProps = {};

const hash = uuidv4().split("-");

export function Login({ socket, config }: any) {
  const [formError, setFormError] = useState("");
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [appLoading, setAppLoading] = useState(true);

  const fieldsKeys = Object.keys(config);
  const emailExists = fieldsKeys.includes("email");
  const telefoneExists = fieldsKeys.includes("telefone");
  const cpfExists = fieldsKeys.includes("cpf");

  // url params
  // const boasVindas = searchParams.get("boas_vindas");
  const autocomplete = searchParams.get("autocomplete");
  const mascot = searchParams.get("mascote");
  const mascotPosition = searchParams.get("mascote_posicao") || "center";
  const company = searchParams.get("empresa");

  const colorText = searchParams.get("color_text");
  const darkColorText = searchParams.get("dark_color_text");
  // const hashChat = searchParams.get("hash_chat");
  // form params
  const tituloLogin = searchParams.get("titulo_login"); // string
  const textoLogin = searchParams.get("texto_login"); // string
  const noCompany = searchParams.get("sem_empresa") === "1"; // string boolean

  // form style
  const noBackground = searchParams.get("no_bg") === "1"; // string boolean
  const containerShadow = searchParams.get("container_shadow"); // string 0%200%208px%204px%20%23cbcbcb
  const containerPadding = searchParams.get("container_padding"); // string numeric
  const containerGap = searchParams.get("container_gap"); // string numeric
  const containerRadius = searchParams.get("container_radius");
  const width = searchParams.get("container_width"); // string numeric
  const fieldsRadius = searchParams.get("input_radius"); // string
  const fieldsBg = searchParams.get("input_bg"); // string
  const fieldsColor = searchParams.get("input_color"); // string
  const ctaRadius = searchParams.get("cta_radius");
  const ctaContent = searchParams.get("cta_content");
  const ctaPaddingY = searchParams.get("cta_padding_y");
  const ctaPaddingX = searchParams.get("cta_padding_x");
  const ctaBackground = searchParams.get("cta_bg");
  const ctaColor = searchParams.get("cta_color");

  const anonymousLogin = !!fieldsKeys.length ? false : true;

  const anonymousCredential = {
    name: "Usuário anônimo " + hash[4].concat(hash[3]),
    identifier: "usuariodesconhecido@email.com.br|" + hash[4].concat(hash[3]),
  };

  const nome = searchParams.get("nome") || "";
  const email = searchParams.get("email") || "";
  const cpf = searchParams.get("cpf") || "";
  const telefone = searchParams.get("telefone") || "";
  const hasParamAuth = Boolean(email || cpf || telefone);

  const hasAllParams = Boolean(
    nome &&
      (emailExists ? Boolean(email) : true) &&
      (cpfExists ? Boolean(cpf) : true) &&
      (telefoneExists ? Boolean(telefone) : true)
  );

  const dispatch = useAppDispatch();
  const httpService = useHttpEscalloChatService();
  const tokenChat = useAppSelector(selectToken);
  const alreadyLoggedByParam = useAppSelector(selectAlreadyLoggedByParam);
  const fields = useRef(fieldsKeys);

  const getFieldValidation = (field: any) => {
    switch (field) {
      case "telefone":
        return {
          phoneNumber: yup
            .string()
            .matches(phoneRegExp, "Formato de telefone inválido")
            .required("Número de telefone é obrigatório"),
        };
      case "cpf":
        return {
          cpf: yup
            .string()
            .matches(cpfRegExp, "Formato de CPF está inválido")
            .test("is-cpf-valid", "CPF informado não existe", isValidCPF)
            .required("CPF é obrigatório"),
        };
      case "email":
        return {
          email: yup
            .string()
            .email("Formato de e-mail inválido")
            .required("E-mail é obrigatório"),
        };
      default:
        return {};
    }
  };

  const validationSchema = yup.object({
    name: yup.string().required("Nome obrigatório"),
    ...fields.current.reduce((acc, current) => {
      return { ...acc, ...getFieldValidation(current) };
    }, {}),
  });

  function onLogin(user: string, identifier: string, cb: any) {
    // dispatch(setCredentialsLoading(true));
    return new Promise((resolve, reject) => {
      httpService
        .auth(user, identifier, tokenChat)
        .then((res) => {
          cb();
          dispatch(setChatState(true));
          dispatch(setCredentials(res));
          socket.attachUserSocket(res);
          // dispatch(setCredentialsLoading(false));
          resolve({ code: 200 });
        })
        .catch((err) => {
          console.error(err, err.res.status);
          if (err.res.status === 401) {
            reject({
              code: err.res.status,
              message: "Usuário já está em uma sessão ativa",
            });
          } else {
            reject({
              code: err.res.status,
              message: "Ocorreu um erro ao efetuar o login",
            });
          }
        });
    });
  }

  function getFormattedValues(cpf: string, email: string, telefone: string) {
    const formattedCPF = cpf.replace(/\D/g, "");
    const formattedEmail = email.toLowerCase();
    const formattedTelefone = telefone.replace(/\D/g, "");
    const formattedValues = fieldsKeys
      // eslint-disable-next-line array-callback-return
      .map((item: string) => {
        switch (item) {
          case "telefone":
            if (formattedTelefone) return formattedTelefone;
            break;
          case "cpf":
            if (formattedCPF) return formattedCPF;
            break;
          case "email":
            if (formattedEmail) return formattedEmail;
            break;
          default:
            break;
        }
      })
      .filter((item) => Boolean(item));
    return formattedValues.join("|");
  }

  const formik = useFormik({
    initialValues: {
      name: "",
      phoneNumber: "(__) _____-____",
      cpf: "___.___.___-__",
      email: "",
      phoneMask: "(99) 9999-9999",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setFormError("");
      const identifier = getFormattedValues(
        values.cpf,
        values.email,
        values.phoneNumber
      );
      try {
        setLoading(true);
        await onLogin(values.name, identifier, () => {
          setLoading(false);
        });
      } catch (error: any) {
        setLoading(false);
        console.error(error.message);
        setFormError(error.message);
      }
    },
  });

  useEffect(() => {
    async function authByParam() {
      if (hasParamAuth && !alreadyLoggedByParam) {
        const identifier = getFormattedValues(cpf, email, telefone);
        if (hasAllParams) {
          try {
            setLoading(true);
            setAppLoading(true);
            await onLogin(nome, identifier, () => {
              dispatch(setAlreadyLoggedByParam(true));
              setAppLoading(false);
              setLoading(false);
            });
          } catch (error: any) {
            dispatch(setAlreadyLoggedByParam(true));
            setLoading(false);
            setAppLoading(false);
            console.error(error.message);
            setFormError(error.message);
          }
        } else {
          setLoading(false);
          setAppLoading(false);
          setFormError(
            "Erro: Todos os parâmetros de URL devem estar presentes"
          );
        }
      } else {
        setLoading(false);
        setAppLoading(false);
      }
    }

    if (anonymousLogin) {
      (async () => {
        try {
          setLoading(true);
          await onLogin(
            anonymousCredential.name,
            anonymousCredential.identifier,
            () => {
              setLoading(false);
            }
          );
        } catch (error: any) {
          setLoading(false);
          console.error(error.message);
          setFormError(error.message);
        }
      })();
    } else {
      authByParam();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function beforeMaskedStateChange({ nextState, currentState }: any) {
    if (currentState && currentState?.value.replace(/\D/g, "").length > 11) {
      return nextState;
    } else if (
      currentState &&
      currentState?.value.replace(/\D/g, "").length >= 10
    ) {
      const { value } = currentState;
      const numbers = value.replace(/\D/g, "");
      if (numbers.length > 10) {
        if (formik.values.phoneMask !== "(99) 99999-9999") {
          formik.setFieldValue("phoneMask", "(99) 99999-9999");
        }
      } else {
        if (formik.values.phoneMask !== "(99) 9999-9999") {
          formik.setFieldValue("phoneMask", "(99) 9999-9999");
        }
      }
      return {
        ...currentState,
      };
    } else {
      return {
        ...nextState,
      };
    }
  }

  const onChange = useCallback((e: any) => {
    setFormError("");
    formik.handleChange(e);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (noBackground) document.body.style.backgroundColor = "transparent";
  }, [noBackground]);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <AppLoading loading={appLoading}>
      <Box
        sx={{
          height: "100%",
          padding: 2,
          ...(!Boolean(noBackground)
            ? matches
              ? {}
              : { background: theme.chat.background }
            : {}),
        }}
      >
        {!Boolean(noBackground) && matches && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              bottom: "40%",
              right: 0,
              left: 0,
              zIndex: -1,
              background: theme.palette.primary.main,
            }}
          ></Box>
        )}

        {!Boolean(noCompany) && matches && (
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "1.25rem",
              textAlign: "left",
              position: "absolute",
              color:
                lightOrDark(
                  matches ? theme.palette.primary.main : theme.chat.background
                ) === "dark"
                  ? "#FFF"
                  : "#fff",
            }}
          >
            {company ? company : "Escallo"}
          </Typography>
        )}

        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            paddingTop: !Boolean(noBackground) ? "1.5em" : "0",
            gap: "0.5em",
            alignItems: "center",
            height: "100%",
          }}
          noValidate
          autoComplete={autocomplete ? "on" : "off"}
          onSubmit={formik.handleSubmit}
        >
          <Box>
            {!Boolean(noCompany) && !matches && (
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "1.25rem",
                  textAlign: "center",
                  width: "100%",
                  color:
                    lightOrDark(
                      matches
                        ? theme.palette.primary.main
                        : theme.chat.background
                    ) === "dark"
                      ? darkColorText
                        ? "#" + darkColorText
                        : "#FFF"
                      : colorText
                      ? "#" + colorText
                      : theme.palette.primary.main,
                }}
              >
                {company ? company : "Escallo"}
              </Typography>
            )}

            {matches && mascot && (
              <div style={{ display: "flex", justifyContent: mascotPosition }}>
                <img
                  src={mascot}
                  alt="mascot-chat"
                  style={{ height: "80px", objectFit: "contain" }}
                />
              </div>
            )}

            {!(tituloLogin === "0") && (
              <Typography
                sx={{
                  fontWeight: "bold",
                  textAlign: matches ? "left" : "center",
                  padding: "0 32px",
                  width: "100%",
                  color:
                    lightOrDark(
                      !noBackground && matches
                        ? theme.palette.primary.main
                        : theme.chat.background
                    ) === "dark"
                      ? darkColorText
                        ? "#" + darkColorText
                        : "#FFF"
                      : colorText
                      ? "#" + colorText
                      : theme.palette.primary.main,
                }}
              >
                {tituloLogin ? tituloLogin : "Bem-vindo ao chat do Escallo!"}
              </Typography>
            )}

            {!formError && !(textoLogin === "0") && (
              <Typography
                sx={{
                  textAlign: matches ? "left" : "center",
                  width: "100%",
                  padding: "0 32px",
                  color:
                    lightOrDark(
                      !noBackground && matches
                        ? theme.palette.primary.main
                        : theme.chat.background
                    ) === "dark"
                      ? darkColorText
                        ? "#" + darkColorText
                        : "#FFF"
                      : colorText
                      ? "#" + colorText
                      : theme.palette.primary.main,
                }}
              >
                {textoLogin ? textoLogin : "Insira seus dados para começar"}
              </Typography>
            )}
          </Box>

          {formError && (
            <Alert
              sx={{
                color:
                  lightOrDark(
                    matches ? theme.palette.primary.main : theme.chat.background
                  ) === "dark"
                    ? theme.palette.error.light
                    : theme.palette.error.dark,
              }}
              variant="outlined"
              severity="error"
            >
              {formError}
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: Number(containerGap) ? `${containerGap}em` : "1.5em",
              padding: Number(containerPadding)
                ? `${Number(containerPadding)}px`
                : 3,
              alignItems: "center",
              borderRadius: Number(containerRadius)
                ? `${Number(containerRadius)}px`
                : 1,
              boxShadow: !containerShadow ? theme.shadows[3] : containerShadow,
              width: "100%",
              maxWidth: !matches
                ? Number(width)
                  ? Number(width)
                  : 340
                : "100%",
              // minWidth: 300,
              background: theme.palette.background.paper,
            }}
          >
            <TextField
              id="name"
              name="name"
              label="Nome"
              type="text"
              required
              variant="outlined"
              value={formik.values?.name}
              placeholder="Como você quer ser chamado?"
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              onChange={onChange}
              fullWidth
              InputProps={{
                sx: {
                  borderRadius:
                    fieldsRadius
                      ?.split(" ")
                      .flatMap((radius) => `${radius}px`)
                      .join(" ") || "0px",
                  backgroundColor: fieldsBg ?? "white",
                  color: fieldsColor ?? "black",
                },
              }}
            />

            {telefoneExists && (
              <InputMask
                mask={formik.values.phoneMask}
                value={formik.values?.phoneNumber}
                maskPlaceholder={"_"}
                beforeMaskedStateChange={beforeMaskedStateChange}
                onChange={onChange}
              >
                <TextField
                  id="phoneNumber"
                  name="phoneNumber"
                  label="Telefone"
                  type="tel"
                  required
                  error={
                    formik.touched.phoneNumber &&
                    Boolean(formik.errors.phoneNumber)
                  }
                  helperText={
                    formik.touched.phoneNumber && formik.errors.phoneNumber
                  }
                  placeholder="Informe seu telefone"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    sx: {
                      backgroundColor: fieldsBg ?? "white",
                      color: fieldsColor ?? "black",
                      borderRadius:
                        fieldsRadius
                          ?.split(" ")
                          .flatMap((radius) => `${radius}px`)
                          .join(" ") || "0px",
                    },
                  }}
                />
              </InputMask>
            )}

            {emailExists && (
              <TextField
                id="email"
                name="email"
                value={formik.values?.email}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                required
                label="E-mail"
                type="email"
                placeholder="email@exemplo.com.br"
                variant="outlined"
                onChange={onChange}
                fullWidth
                InputProps={{
                  sx: {
                    backgroundColor: fieldsBg ?? "white",
                    color: fieldsColor ?? "black",
                    borderRadius:
                      fieldsRadius
                        ?.split(" ")
                        .flatMap((radius) => `${radius}px`)
                        .join(" ") || "0px",
                  },
                }}
              />
            )}

            {cpfExists && (
              <InputMask
                mask={"999.999.999-99"}
                value={formik.values?.cpf}
                maskPlaceholder={"_"}
                onChange={onChange}
              >
                <TextField
                  id="cpf"
                  name="cpf"
                  label="CPF"
                  type="text"
                  required
                  error={formik.touched.cpf && Boolean(formik.errors.cpf)}
                  helperText={formik.touched.cpf && formik.errors.cpf}
                  placeholder="Informe seu CPF"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    sx: {
                      backgroundColor: fieldsBg ?? "white",
                      color: fieldsColor ?? "black",
                      borderRadius:
                        fieldsRadius
                          ?.split(" ")
                          .flatMap((radius) => `${radius}px`)
                          .join(" ") || "0px",
                    },
                  }}
                />
              </InputMask>
            )}

            <LoadingButton
              loading={Boolean(loading)}
              variant="contained"
              fullWidth
              sx={{
                px: Number(ctaPaddingX) ? `${Number(ctaPaddingX)}px` : 4,
                py: Number(ctaPaddingY) ? `${Number(ctaPaddingY)}px` : 1.5,
                transition: "all 0.4s",
                boxShadow: "none",
                ":hover": {
                  boxShadow: "none",
                  backgroundColor: ctaBackground
                    ? darken(`#${ctaBackground}`, 0.1)
                    : darken(theme.palette.primary.main, 0.1),
                },
                color: ctaColor
                  ? `#${ctaColor}`
                  : theme.palette.primary.contrastText,
                backgroundColor: ctaBackground
                  ? `#${ctaBackground}`
                  : theme.palette.primary.main,
                borderRadius:
                  ctaRadius
                    ?.split(" ")
                    .flatMap((radius) => `${radius}px`)
                    .join(" ") || "0px",
              }}
              type="submit"
            >
              {ctaContent ? ctaContent : "Começar"}
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </AppLoading>
  );
}
